import React, { useMemo } from 'react';
import { UserAvatarAndName } from '../../../../molecules/UserAvatarAndName';
import { useSelector } from 'react-redux';
import { MemberMovements, WhereaboutsGroupType } from '@features/whereabouts/models/movements.models';
import EditMovementsPanel from '../edit-movements-panel/EditMovementsPanel';
import {
  selectEditingWhereabouts,
  selectFocusedGroupId,
  selectFocusedUserId
} from '@features/whereabouts/store/editMovements.duck';
import './user-row.scss';
import { MovementsName, UsersMovements, UsersMovementsRow } from "../StructuralComponents";
import { Column } from '../../../../atoms/StructuralLayout';
import MovementsWeek from "../team-member-row/MovementsWeek";
import { UserResponsibilityBadges } from "../../../../molecules/UserResponsibilityBadges";
import { canEditAnyUser, canUserEditUser } from '@shared/utils/AccessControl';
import { SetWhereaboutsButton } from "./SetWhereaboutsButton";
import moment from "moment";
import { ResyncCalendarButton } from "../../../../molecules/ResyncCalendarButton";
import { User } from '@shared/models/user.models';

interface Props {
  member: MemberMovements;
  enableSetMovements: boolean;
  currentUser?: User
  groupId?: WhereaboutsGroupType;
}

export default function WhereaboutsRow(props: Props) {
  const {member, currentUser} = props;
  const focusedUserId = useSelector(selectFocusedUserId);
  const focusedGroupId = useSelector(selectFocusedGroupId);
  const editingWhereabouts = useSelector(selectEditingWhereabouts);

  const isEditing = useMemo(() => !!focusedUserId && !!member?.id && focusedUserId === member.id && focusedGroupId === props.groupId, [focusedGroupId, focusedUserId, member?.id, props.groupId]);
  const canEditUsersMovements = useMemo(() => canUserEditUser(currentUser, member), [currentUser, member]);
  const isCurrentUser = useMemo(() => currentUser?.id === member?.id, [currentUser, member]);
  const usesCalendarSync = useMemo(() => currentUser?.enableOutlookSync, [currentUser]);
  const enableSetMovements = useMemo(() => canEditUsersMovements || isCurrentUser, [canEditUsersMovements, isCurrentUser]);
  const hasBeenEdited = useMemo(() => isCurrentUser && currentUser?.whereaboutsEdited, [isCurrentUser, currentUser]);
  const carParking = useMemo(() => member?.carParking ?? [], [member]);

  if (!member?.id) return null;

  const dataTestValue =  `avatar-${member.firstName}-${member.lastName}`.replace('/ /g', '-');

  return (
    <UsersMovementsRow className="wallchart__row" isEditting={isEditing} data-test={dataTestValue}>
      <Column>
        <UsersMovements>
          <MovementsName>
            <UserAvatarAndName colour={isCurrentUser ? 'blue' : 'darkBlue'}
                               firstName={member.firstName} lastName={member.lastName}
                               userId={member.id} showUserHoverInfo={true} showEditLink={canEditAnyUser(currentUser)}/>
            <UserResponsibilityBadges user={member} />
          </MovementsName>

          <MovementsWeek movements={isEditing ? editingWhereabouts : member.whereabouts}
                         userId={member.id}
                         isEditing={isEditing}
                         areUsersMovements={isCurrentUser}
                         enableSetMovements={enableSetMovements}
                         carParking={carParking}
                         dateFrom={member.dateFrom}
                         currentUser={currentUser}
                         dateTo={member.dateTo}
                         birthdayDay={currentUser?.companyEntity.showBirthdays ? member.birthdayDay : undefined}
                         birthdayMonth={currentUser?.companyEntity.showBirthdays ? member.birthdayMonth : undefined}
                         sevenDayWeekEnabled={currentUser?.companyEntity.sevenDayWhereaboutsWeekEnabled}
                         groupId={props.groupId} />

          {/* Mobile set movements button */}
          {enableSetMovements && isCurrentUser && !hasBeenEdited &&
              <SetWhereaboutsButton userId={props.member.id} isCurrentUser={isCurrentUser}
                                                   isEditing={isEditing}
                                                   className="wallchartRow__editButton--mobile"/>}
        </UsersMovements>

        {enableSetMovements && isCurrentUser && <>
          {hasBeenEdited && usesCalendarSync ?
            <ResyncCalendarButton date={moment(member.dateFrom)} /> :
            <SetWhereaboutsButton userId={props.member.id} isCurrentUser={isCurrentUser}
                                  isEditing={isEditing} />}
        </>}

      </Column>

      {enableSetMovements && isEditing && <EditMovementsPanel isEditing={isEditing} userId={props.member.id}/>}
    </UsersMovementsRow>
  )
}
